/**
 * Wire types for the Contacts advanced filter.
 *
 * These mirror the GraphQL `ContactAdvancedFilterInput` shape exactly, because
 * the filter is sent to the resolver as-is and evaluated in Spanner. Nothing in
 * this module filters contacts locally — a filtered page whose total and paging
 * came from the server but whose rows were trimmed in the browser would show
 * the wrong counts.
 */

/** Comparison operators, as the `ContactFilterOperator` GraphQL enum spells them. */
export type ContactFilterOperator =
  | 'EQ'
  | 'NEQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'STARTS_WITH'
  | 'IN'
  | 'NOT_IN'
  | 'IS_EMPTY'
  | 'IS_NOT_EMPTY';

export type ContactFilterCombinator = 'AND' | 'OR';

/** Value kinds a filterable field can have; drives which input widget renders. */
export type ContactFilterDataType = 'STRING' | 'ENUM' | 'NUMBER' | 'BOOLEAN' | 'DATETIME' | 'TAGS';

export interface ContactFilterFieldOption {
  value: string;
  label: string;
}

/** One entry from the server's field catalogue (`contactFilterFields`). */
export interface ContactFilterField {
  name: string;
  label: string;
  group: string;
  dataType: ContactFilterDataType;
  operators: ContactFilterOperator[];
  options: ContactFilterFieldOption[];
}

export interface ContactFilterCondition {
  field: string;
  operator: ContactFilterOperator;
  /** Operand for single-value operators. Sent as a string; the server coerces. */
  value?: string | null;
  /** Operands for IN / NOT_IN. */
  values?: string[] | null;
}

/**
 * What the builder edits: one flat list of conditions joined by a single
 * AND or OR.
 *
 * The GraphQL input nests conditions inside groups so the server can express
 * `(A AND B) OR (C AND D)`, but the UI deliberately does not — two levels of
 * combinator read as a puzzle. `toGraphQLFilter` wraps this into the single
 * group the wire format expects.
 */
export interface ContactAdvancedFilter {
  conditions: ContactFilterCondition[];
  combinator: ContactFilterCombinator;
}

/** The nested shape `advancedFilter` is sent as. Built only by `toGraphQLFilter`. */
export interface ContactAdvancedFilterInput {
  groups: { conditions: ContactFilterCondition[]; combinator: ContactFilterCombinator }[];
  combinator: ContactFilterCombinator;
}

/** Operators that read `values` rather than `value`. */
export const LIST_OPERATORS: ReadonlySet<ContactFilterOperator> = new Set<ContactFilterOperator>([
  'IN',
  'NOT_IN',
]);

/** Operators that take no operand at all. */
export const NULLARY_OPERATORS: ReadonlySet<ContactFilterOperator> = new Set<ContactFilterOperator>(
  ['IS_EMPTY', 'IS_NOT_EMPTY']
);

const BASE_LABELS: Record<ContactFilterOperator, string> = {
  EQ: 'is',
  NEQ: 'is not',
  GT: 'greater than',
  GTE: 'greater than or equal',
  LT: 'less than',
  LTE: 'less than or equal',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'does not contain',
  STARTS_WITH: 'starts with',
  IN: 'is any of',
  NOT_IN: 'is none of',
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty',
};

/** Fields whose operand is a picker, so one-or-many is not a choice to make. */
const usesPicker = (field: ContactFilterField): boolean =>
  field.dataType === 'ENUM' || field.dataType === 'TAGS' || field.name === 'assignedAgentId';

// Per-type wording. "greater than" is right for a score and wrong for a date,
// and "is empty" is right for text and odd for a number.
const DATETIME_LABELS: Partial<Record<ContactFilterOperator, string>> = {
  GT: 'after',
  GTE: 'on or after',
  LT: 'before',
  LTE: 'on or before',
  IS_EMPTY: 'has no date',
  IS_NOT_EMPTY: 'has a date',
};

const NUMBER_LABELS: Partial<Record<ContactFilterOperator, string>> = {
  IS_EMPTY: 'has no value',
  IS_NOT_EMPTY: 'has a value',
};

const TAG_LABELS: Partial<Record<ContactFilterOperator, string>> = {
  IN: 'has any of',
  NOT_IN: 'has none of',
  IS_EMPTY: 'has none',
  IS_NOT_EMPTY: 'has any',
};

const ASSIGNEE_LABELS: Partial<Record<ContactFilterOperator, string>> = {
  IS_EMPTY: 'is unassigned',
  IS_NOT_EMPTY: 'is assigned',
};

/**
 * Label for an operator *on a given field*.
 *
 * A single shared label table cannot work here. `CONTAINS` is a substring test
 * on an email and an exact tag test on `tags`, so calling both "contains"
 * invites exactly the wrong reading; likewise `GT` is "greater than" on a
 * score and "after" on a date.
 */
export const operatorLabel = (
  operator: ContactFilterOperator,
  field: ContactFilterField | undefined
): string => {
  if (!field) return BASE_LABELS[operator];
  if (field.name === 'assignedAgentId') return ASSIGNEE_LABELS[operator] ?? BASE_LABELS[operator];
  if (field.dataType === 'TAGS') return TAG_LABELS[operator] ?? BASE_LABELS[operator];
  if (field.dataType === 'DATETIME') return DATETIME_LABELS[operator] ?? BASE_LABELS[operator];
  if (field.dataType === 'NUMBER') return NUMBER_LABELS[operator] ?? BASE_LABELS[operator];
  return BASE_LABELS[operator];
};

/**
 * Operators worth showing for a field, out of everything the server accepts.
 *
 * Two overlaps made the dropdown confusing:
 *
 * - On a picker field, `is` and `is any of` ask the same question — the widget
 *   is a multi-select, so picking one value already means "is". Only the list
 *   form is offered, which also kills the `contains` / `is any of` collision
 *   on `tags`, where both meant "has this tag".
 * - On free text, `is any of` needed a comma-separated box and sat right next
 *   to `contains`, which does something genuinely different. Dropping it
 *   leaves `is` / `contains` / `starts with`, which read unambiguously.
 *
 * The server still accepts the hidden ones; this is presentation only.
 */
export const visibleOperators = (
  field: ContactFilterField | undefined
): ContactFilterOperator[] => {
  if (!field) return [];
  const hidden: ContactFilterOperator[] = usesPicker(field)
    ? ['EQ', 'NEQ', 'CONTAINS', 'NOT_CONTAINS', 'STARTS_WITH']
    : ['IN', 'NOT_IN'];
  const visible = field.operators.filter((operator) => !hidden.includes(operator));
  return visible.length > 0 ? visible : field.operators;
};

/**
 * Catalogue values offered to the user as a single choice.
 *
 * `bounced` is a hard bounce and `soft_bounce` a transient one, but "did this
 * address bounce" is the question people actually ask. Offering a combined
 * entry *and* both members would be three overlapping choices for two states.
 *
 * `active` and `valid` are folded for a different reason: the consent read
 * model treats them as the same state, so they are two spellings of one
 * answer and picking either alone silently drops the other's contacts.
 *
 * A group only replaces members the catalogue actually returns, so a group
 * whose members the server has already merged into one option is inert
 * rather than wrong.
 *
 * The key is prefixed so it can never collide with a catalogue value, and it
 * exists only in UI state — `expandGroupedValues` turns it back into the
 * values the server accepts before the filter is sent.
 */
const VALUE_GROUPS: Record<string, { key: string; label: string; values: string[] }[]> = {
  emailStatus: [
    { key: 'group:active_valid', label: 'Active / Valid', values: ['active', 'valid'] },
    { key: 'group:bounced', label: 'Bounced', values: ['bounced', 'soft_bounce'] },
  ],
};

/**
 * The enum values worth offering for a field.
 *
 * A group replaces its members in place, at the position of the first one, so
 * the catalogue's ordering survives.
 */
export const valueOptions = (field: ContactFilterField | undefined): ContactFilterFieldOption[] => {
  if (!field) return [];
  const groups = VALUE_GROUPS[field.name] ?? [];
  if (groups.length === 0) return field.options;

  const emitted = new Set<string>();
  const options: ContactFilterFieldOption[] = [];
  for (const option of field.options) {
    const group = groups.find((candidate) => candidate.values.includes(option.value));
    if (!group) {
      options.push(option);
      continue;
    }
    if (!emitted.has(group.key)) {
      options.push({ value: group.key, label: group.label });
      emitted.add(group.key);
    }
  }
  return options;
};

/** Turn any group keys back into the catalogue values the server validates. */
export const expandGroupedValues = (fieldName: string, values: string[]): string[] => {
  const groups = VALUE_GROUPS[fieldName] ?? [];
  if (groups.length === 0) return values;
  const expanded = values.flatMap(
    (value) => groups.find((group) => group.key === value)?.values ?? [value]
  );
  return Array.from(new Set(expanded));
};

const emptyValue = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim() === '';

/**
 * True when a condition is complete enough for the server to compile it.
 *
 * Incomplete rows are dropped rather than sent, so a half-built row in the UI
 * never turns into a resolver error the user has to decode.
 */
export const isConditionComplete = (condition: ContactFilterCondition): boolean => {
  if (!condition.field || !condition.operator) return false;
  if (NULLARY_OPERATORS.has(condition.operator)) return true;
  if (LIST_OPERATORS.has(condition.operator)) {
    return (condition.values ?? []).some((v) => !emptyValue(v));
  }
  // The server compiles this one by stripping non-digits and rejects what is
  // left if it is empty, so a value of "(" is not a filter that returns
  // nothing — it is a filter that cannot run.
  if (condition.field === 'phone' && condition.operator === 'STARTS_WITH') {
    return /\d/.test(condition.value ?? '');
  }
  return !emptyValue(condition.value);
};

/**
 * Strip the filter down to what the server can evaluate, or `undefined` when
 * nothing is left. Passing `undefined` as the GraphQL variable is what makes
 * "no advanced filter" and "an empty builder" behave identically.
 */
export const toGraphQLFilter = (
  filter: ContactAdvancedFilter | null | undefined
): ContactAdvancedFilterInput | undefined => {
  if (!filter) return undefined;

  // Each operator reads exactly one of `value` / `values` / neither, and the
  // server rejects a condition carrying the wrong one, so only the relevant
  // key is emitted.
  const conditions = filter.conditions.filter(isConditionComplete).map((condition) => {
    if (NULLARY_OPERATORS.has(condition.operator)) {
      return { field: condition.field, operator: condition.operator };
    }
    if (LIST_OPERATORS.has(condition.operator)) {
      return {
        field: condition.field,
        operator: condition.operator,
        values: expandGroupedValues(
          condition.field,
          (condition.values ?? []).filter((v) => !emptyValue(v))
        ),
      };
    }
    return { field: condition.field, operator: condition.operator, value: condition.value };
  });

  if (conditions.length === 0) return undefined;

  return {
    groups: [{ conditions, combinator: filter.combinator }],
    combinator: filter.combinator,
  };
};

/** Number of conditions the filter will actually apply. */
export const countActiveConditions = (filter: ContactAdvancedFilter | null | undefined): number =>
  (filter?.conditions ?? []).filter(isConditionComplete).length;

export const emptyAdvancedFilter = (): ContactAdvancedFilter => ({
  conditions: [],
  combinator: 'AND',
});

/**
 * Catalogue fields that only exist for an org with Flywheel: SMS is a
 * Flywheel-only channel, and the server drops `inFlywheel` from its catalogue
 * and rejects it in a filter once the org loses Flywheel.
 */
export const FLYWHEEL_ONLY_FIELDS: ReadonlySet<string> = new Set(['smsConsent', 'inFlywheel']);

/**
 * The filter as the org can run it. A condition saved while Flywheel was on
 * stays in state after it is turned off; sending it would narrow the list on a
 * card the builder no longer shows, or fail the whole query for `inFlywheel`.
 */
export const withoutFlywheelOnlyConditions = (
  filter: ContactAdvancedFilter | null | undefined,
  flywheelEnabled: boolean
): ContactAdvancedFilter | null | undefined => {
  if (!filter || flywheelEnabled) return filter;
  return {
    ...filter,
    conditions: filter.conditions.filter((c) => !FLYWHEEL_ONLY_FIELDS.has(c.field)),
  };
};
